import { SubscriptionStatus, UserSubscription } from 'types/subscription';

export const STRIPE_ENDED_STATUSES = [
  'canceled',
  'unpaid',
  'incomplete_expired',
] as const;

export type StripeEndedStatus = typeof STRIPE_ENDED_STATUSES[number];

/** Sticky + profile status billing-attention reasons, highest priority first. */
export const BILLING_ATTENTION_REASONS = [
  'unpaid',
  'past_due',
  'overdue_invoice',
] as const;

export type BillingAttentionReason = typeof BILLING_ATTENTION_REASONS[number];

function isSendInvoiceCollection(
  subscription?: UserSubscription | null,
): boolean {
  return subscription?.stripeCollectionMethod === 'send_invoice';
}

/**
 * Invoice billing past due: Stripe marks the subscription `past_due` when a
 * `send_invoice` invoice is unpaid after its due date.
 */
export function isOverdueInvoice(
  subscription?: UserSubscription | null,
): boolean {
  if (!subscription) return false;
  const stripeStatus = subscription.stripeSubscriptionStatus?.toLowerCase();
  if (stripeStatus === 'overdue_invoice') return true;
  return stripeStatus === 'past_due' && isSendInvoiceCollection(subscription);
}

export function isPaidPlanTrialPeriod(
  subscription?: UserSubscription | null,
): boolean {
  if (!subscription?.subscriptionPlan) return false;

  if (subscription.planId === SubscriptionStatus.TRIAL) return false;
  if (subscription.stripeSubscriptionStatus === 'trialing') return true;
  if (subscription.status === SubscriptionStatus.TRIAL) return true;
  return false;
}

export function isStripeSubscriptionFullyEnded(
  subscription?: UserSubscription | null,
): boolean {
  const status = subscription?.stripeSubscriptionStatus;
  if (!status) return false;
  return (STRIPE_ENDED_STATUSES as readonly string[]).includes(status);
}

/** App-level cancelled when Stripe has not fully ended the subscription. */
export function isLegacyAppCancelled(
  subscription?: UserSubscription | null,
): boolean {
  if (!subscription) return false;
  if (isStripeSubscriptionFullyEnded(subscription)) return false;
  return (
    subscription.status === SubscriptionStatus.CANCELLED &&
    subscription.subscriptionId !== SubscriptionStatus.MIGRATED
  );
}

/**
 * User canceled but still has access until period end (no renewal).
 * Billing attention for routine payment issues is suppressed in this state
 * (overdue invoice / pause / unpaid are handled separately).
 */
export function isUserScheduledCancel(
  subscription?: UserSubscription | null,
): boolean {
  if (!subscription) return false;
  if (isLegacyAppCancelled(subscription)) return true;
  return (
    subscription.stripeCancelAtPeriodEnd === true &&
    !isStripeSubscriptionFullyEnded(subscription)
  );
}

/**
 * Highest-priority billing attention for sticky + profile status banners.
 * Driven by stripeSubscriptionStatus + stripeCollectionMethod.
 * Suppressed for scheduled cancel except unpaid, pause, and overdue invoice.
 */
export function getBillingAttentionReason(
  subscription?: UserSubscription | null,
): BillingAttentionReason | null {
  if (!subscription) return null;

  const stripeStatus = subscription.stripeSubscriptionStatus?.toLowerCase();

  if (stripeStatus === 'unpaid') return 'unpaid';

  if (isOverdueInvoice(subscription)) return 'overdue_invoice';

  if (isUserScheduledCancel(subscription)) return null;

  if (stripeStatus === 'past_due') return 'past_due';

  return null;
}

export function getStripeSubscriptionEndIso(
  subscription?: UserSubscription | null,
): string | null | undefined {
  const sub = subscription;
  if (!sub) return undefined;

  if (isStripeSubscriptionFullyEnded(sub)) {
    return (
      sub.stripeEndedAt ||
      sub.stripeCancelAt ||
      sub.stripeCurrentPeriodEnd ||
      sub.endDate ||
      undefined
    );
  }

  if (
    sub.stripeSubscriptionStatus === 'active' &&
    sub.stripeCancelAtPeriodEnd
  ) {
    return sub.stripeCancelAt || sub.stripeCurrentPeriodEnd || undefined;
  }

  return sub.stripeCurrentPeriodEnd || sub.endDate || undefined;
}
